import { VISA_TOTAL_MONTHS } from "@/constants/common";
import dayjs, { Dayjs, isDayjs } from "dayjs";

type TResidencePeriods = {
  elapsed: string;
  remaining: string;
};

/**
 * Format year/month period string
 * e.g. { years: 2, months: 3 } → "2年3か月", { years: 0, months: 0 } → "0年0か月"
 */
const formatPeriod = (years: number, months: number): string => {
  if (years === 0 && months === 0) return "0年0か月";
  let result = "";
  if (years > 0) result += `${years}年`;
  if (months > 0) result += `${months}か月`;
  return result || "0年0か月";
};

/**
 * Calculate precise year/month diff between two dates (from → to)
 * Uses day-level comparison for accuracy
 */
const calcYearMonthDiff = (from: Dayjs, to: Dayjs): { years: number; months: number } => {
  let yearDiff = to.year() - from.year();
  let monthDiff = to.month() - from.month();

  if (from.date() > to.date()) {
    monthDiff -= 1;
  }
  if (monthDiff < 0) {
    yearDiff -= 1;
    monthDiff += 12;
  }

  return { years: Math.max(0, yearDiff), months: Math.max(0, monthDiff) };
};

/**
 * Calculate elapsed and remaining periods for 1号 residence status
 * - expiryDate = startDate + 5 years + blankPeriod
 * - Remaining: calculated with day-level precision (today → expiryDate)
 * - Elapsed: derived from totalMonths - remainingMonths to ensure they sum correctly
 */
export const calculateResidencePeriods = (
  clause1StartDate?: Dayjs | string | null,
  blankPeriodMonths?: number | string | null,
  emptyValue = "自動入力内容 (readonly)",
): TResidencePeriods => {
  if (!clause1StartDate) {
    return { elapsed: emptyValue, remaining: emptyValue };
  }

  const startDate = dayjs(clause1StartDate);
  if (!startDate.isValid()) {
    return { elapsed: emptyValue, remaining: emptyValue };
  }

  const blankPeriod = Number(blankPeriodMonths) || 0;
  const totalMonths = VISA_TOTAL_MONTHS + blankPeriod;
  const today = dayjs().add(1, "day");
  const expiryDate = startDate.add(5, "year").add(blankPeriod, "month");

  // Not started yet
  if (startDate.isAfter(today) || startDate.isSame(today, "day")) {
    return { elapsed: emptyValue, remaining: emptyValue };
  }

  // Expired
  if (expiryDate.isBefore(today) || expiryDate.isSame(today, "day")) {
    return {
      elapsed: formatPeriod(Math.floor(totalMonths / 12), totalMonths % 12),
      remaining: "0年0か月",
    };
  }

  // In progress: calculate remaining first, then derive elapsed
  const remaining = calcYearMonthDiff(today, expiryDate);
  const remainingTotalMonths = remaining.years * 12 + remaining.months;
  const elapsedTotalMonths = totalMonths - remainingTotalMonths;

  return {
    elapsed: formatPeriod(Math.floor(elapsedTotalMonths / 12), elapsedTotalMonths % 12),
    remaining: formatPeriod(remaining.years, remaining.months),
  };
};

/**
 * Get the next Monday (or today if today is Monday)
 * dayjs: 0=Sunday, 1=Monday
 */
export const getNextOrCurrentMonday = (): Dayjs => {
  const today = dayjs();
  const dayOfWeek = today.day();
  if (dayOfWeek === 1) return today;
  // Days until next Monday: (8 - dayOfWeek) % 7, but Sunday(0) → 1
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  return today.add(daysUntilMonday, "day");
};

/**
 * Calculate available learning weeks
 */
export const calculateAvailableLearningWeeks = (
  learningStartDate: dayjs.Dayjs | string | null | undefined,
  examScheduledYearMonth: dayjs.Dayjs | string | null | undefined
): number | null => {
  if (!learningStartDate || !examScheduledYearMonth) return null;

  const startDate = dayjs(learningStartDate);
  // End date = last day of the month before examScheduledYearMonth
  const examMonth = isDayjs(examScheduledYearMonth)
    ? examScheduledYearMonth
    : dayjs(examScheduledYearMonth, "YYYY-MM");
  const endDate = examMonth.subtract(1, "month").endOf("month");

  if (!startDate.isValid() || !endDate.isValid()) return null;

  const diffDays = endDate.diff(startDate, "day");
  const diffWeeks = Math.floor(diffDays / 7);

  return diffWeeks > 0 ? diffWeeks : 0;
};
